import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Phone, Linkedin, MessageCircle, Instagram, Facebook, MapPin } from "lucide-react";
import { LogoEmblem } from "@/components/Logo";
import { DISCLAIMER } from "@/data/constants";

const QUICK_LINKS = [
  { label: "Home", to: "/" },
  { label: "Opportunities", to: "/opportunities" },
  { label: "Bespoke Sourcing", to: "/bespoke-sourcing" },
  { label: "What We Source", to: "/#what-we-source" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Footer({ settings }) {
  const c = settings?.contact || {};
  const regions = settings?.content?.footprint_regions || ["Africa", "Gulf", "Caribbean", "UK", "US"];
  const navigate = useNavigate();
  const location = useLocation();

  const go = (to) => {
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") { navigate("/"); setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300); }
      else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else { navigate(to); window.scrollTo({ top: 0, behavior: "smooth" }); }
  };
  return (
    <footer data-testid="footer" className="relative border-t border-gold/15 bg-obsidian px-6 pt-16 lg:px-10">
      <div className="mx-auto max-w-7xl">
        {/* Top columns */}
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1.1fr_1fr]">
          <div>
            <LogoEmblem size={124} spin />
            <p className="mt-5 max-w-xs font-serif text-sm italic text-softgrey/80">
              Exclusive Deals. Strategically Sourced.
            </p>
          </div>

          <div>
            <div className="font-sans text-[11px] tracking-luxe uppercase text-gold">Quick Links</div>
            <div className="mt-5 flex flex-col gap-3">
              {QUICK_LINKS.map((l) => (
                <button key={l.label} onClick={() => go(l.to)} data-testid={`footer-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-left font-sans text-sm text-softgrey transition-colors hover:text-warmwhite">
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="font-sans text-[11px] tracking-luxe uppercase text-gold">Contact</div>
            <div className="mt-5 flex flex-col gap-3">
              <a href={`mailto:${c.email || ""}`} className="flex items-center gap-3 font-sans text-sm text-softgrey hover:text-warmwhite" data-testid="footer-email">
                <Mail size={15} className="text-gold" /> {c.email || "info@goldstonesourcing.com"}
              </a>
              {c.phone && (
                <a href={`tel:${(c.phone || "").replace(/\s+/g, "")}`} className="flex items-center gap-3 font-sans text-sm text-softgrey hover:text-warmwhite" data-testid="footer-phone">
                  <Phone size={15} className="text-gold" /> {c.phone}
                </a>
              )}
              {c.address && (
                <div className="flex items-start gap-3 font-sans text-sm text-softgrey" data-testid="footer-address">
                  <MapPin size={15} className="mt-0.5 shrink-0 text-gold" /> <span>{c.address}</span>
                </div>
              )}
              {c.whatsapp && (
                <a href={`https://wa.me/${(c.whatsapp || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 font-sans text-sm text-softgrey hover:text-warmwhite" data-testid="footer-whatsapp-text">
                  <MessageCircle size={15} className="text-gold" /> {c.whatsapp}
                </a>
              )}
            </div>
          </div>

          <div>
            <div className="font-sans text-[11px] tracking-luxe uppercase text-gold">Follow Us</div>
            <div className="mt-5 flex items-center gap-4">
              {c.linkedin && (
                <a href={c.linkedin} target="_blank" rel="noreferrer" className="text-softgrey transition-colors hover:text-gold" data-testid="footer-linkedin" aria-label="LinkedIn"><Linkedin size={18} /></a>
              )}
              {c.whatsapp && (
                <a href={`https://wa.me/${(c.whatsapp || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-softgrey transition-colors hover:text-gold" data-testid="footer-whatsapp" aria-label="WhatsApp"><MessageCircle size={18} /></a>
              )}
              <a href={`mailto:${c.email || ""}`} className="text-softgrey transition-colors hover:text-gold" aria-label="Email"><Mail size={18} /></a>
              {c.instagram && (<a href={c.instagram} target="_blank" rel="noreferrer" className="text-softgrey transition-colors hover:text-gold" aria-label="Instagram"><Instagram size={18} /></a>)}
              {c.facebook && (<a href={c.facebook} target="_blank" rel="noreferrer" className="text-softgrey transition-colors hover:text-gold" aria-label="Facebook"><Facebook size={18} /></a>)}
            </div>
          </div>
        </div>

        {/* Global Footprint panel */}
        <div className="mt-14 border border-gold/15 bg-seablue/15 px-6 py-12 text-center" data-testid="footer-global-footprint">
          <div className="mx-auto flex max-w-xs items-center justify-center gap-3">
            <span className="h-px flex-1 bg-gold/40" /><MapPin size={15} className="text-gold" /><span className="h-px flex-1 bg-gold/40" />
          </div>
          <h4 className="mt-5 font-sans text-sm tracking-luxe uppercase text-warmwhite">Global Footprint</h4>
          <p className="mx-auto mt-3 max-w-md font-sans text-xs leading-relaxed text-softgrey">
            Multi-market property, commercial, material and equipment sourcing across selected strategic markets.
          </p>
          <div className="mx-auto mt-10 grid max-w-4xl grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-5">
            {regions.map((r) => (
              <div key={r} className="flex flex-col items-center gap-3" data-testid={`footprint-${r.toLowerCase()}`}>
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/40"><MapPin size={15} className="text-gold" /></span>
                <span className="font-sans text-[11px] tracking-luxe uppercase text-softgrey">{r}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="gold-hairline my-10" />
        <p className="mx-auto max-w-3xl text-center font-sans text-[11px] leading-relaxed text-softgrey/60">{DISCLAIMER}</p>

        {/* Bottom bar */}
        <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-gold/10 py-8 md:flex-row">
          <span className="font-sans text-xs text-softgrey/60">
            © {new Date().getFullYear()} Goldstone Sourcing Ltd. All rights reserved.{c.company_registration ? ` · ${c.company_registration}` : ""}
          </span>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="font-sans text-xs text-softgrey/60 hover:text-warmwhite" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="font-sans text-xs text-softgrey/60 hover:text-warmwhite" data-testid="footer-terms">Terms of Service</Link>
            <Link to="/complaints" className="font-sans text-xs text-softgrey/60 hover:text-warmwhite" data-testid="footer-complaints">Complaints Procedure</Link>
            <Link to="/admin/login" className="font-sans text-xs text-softgrey/30 hover:text-gold" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
